<template>
  <div class="display" :class="{ 'display--error': !!store.error }">
    <div class="display__toolbar">
      <button
        class="icon-btn"
        :class="{ active: store.activePanel === 'history' }"
        aria-label="Historique"
        :aria-pressed="store.activePanel === 'history'"
        title="Historique"
        @click="store.setPanel('history')"
      >
        🕒
      </button>
      <button
        class="icon-btn"
        :class="{ active: store.activePanel === 'graph' }"
        aria-label="Graphique"
        :aria-pressed="store.activePanel === 'graph'"
        title="Tracer une fonction"
        @click="store.setPanel('graph')"
      >
        📈
      </button>
      <button
        class="icon-btn"
        :class="{ active: store.activePanel === 'settings' }"
        aria-label="Paramètres"
        :aria-pressed="store.activePanel === 'settings'"
        title="Paramètres"
        @click="store.setPanel('settings')"
      >
        ⚙️
      </button>
      <div class="spacer" />
      <button
        class="icon-btn"
        aria-label="Changer de thème"
        title="Thème clair / sombre"
        @click="store.toggleTheme()"
      >
        {{ store.theme === 'dark' ? '🌙' : '☀️' }}
      </button>
      <button
        class="icon-btn"
        aria-label="Effacer le dernier caractère"
        title="Effacer"
        :disabled="!store.hasInput"
        @click="store.backspace()"
      >
        ⌫
      </button>
      <button
        class="mode-btn"
        :class="{ active: store.isScientific }"
        aria-description="Activer ou désactiver le mode scientifique"
        title="Mode scientifique"
        :aria-pressed="store.isScientific"
        @click="store.toggleScientific()"
      >
        <span>SCI</span><small>{{ store.isScientific ? 'ON' : 'OFF' }}</small>
      </button>
    </div>

    <div
      class="display__screen"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="display__input" :title="store.display">
        {{ store.display }}
      </div>
      <transition name="fade">
        <div v-if="secondary" class="display__preview" :class="secondaryClass">
          {{ secondary }}
        </div>
      </transition>
      <div class="display__actions">
        <button
          type="button"
          class="display__action"
          aria-label="Copier le résultat"
          @click="copyDisplay"
        >
          Copier
        </button>
        <button
          type="button"
          class="display__action"
          aria-label="Partager le résultat"
          @click="shareDisplay"
        >
          Partager
        </button>
        <span
          v-if="actionMessage"
          class="display__action-message"
          role="status"
          >{{ actionMessage }}</span
        >
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useCalculator } from '../stores/calculator.js';

const store = useCalculator();

// Ligne secondaire : erreur > aperçu live > dernier résultat.
const secondary = computed(() => {
  if (store.error) return store.error;
  if (store.preview && store.hasInput) return '= ' + store.preview;
  return '';
});

const secondaryClass = computed(() => ({
  'is-error': !!store.error,
}));

const actionMessage = ref('');
let actionTimer;

onBeforeUnmount(() => clearTimeout(actionTimer));

function announce(message) {
  actionMessage.value = message;
  clearTimeout(actionTimer);
  actionTimer = setTimeout(() => {
    actionMessage.value = '';
  }, 2200);
}

async function copyDisplay() {
  const text = store.input || store.result || store.display;

  try {
    await navigator.clipboard.writeText(text);
    announce('Résultat copié.');
  } catch {
    announce('Copie indisponible dans ce navigateur.');
  }
}

async function shareDisplay() {
  const text = store.input || store.result || store.display;
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('expr', text.slice(0, 500));
  const shareUrl = url.toString();
  if (navigator.share) {
    try {
      await navigator.share({ title: 'NovaCalc', text, url: shareUrl });
      announce('Résultat partagé.');
      return;
    } catch (error) {
      if (error?.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(shareUrl);
    announce('Lien de partage copie.');
  } catch {
    announce('Partage indisponible dans ce navigateur.');
  }
}
</script>

<style scoped>
.display {
  background: linear-gradient(155deg, var(--screen-well), var(--surface-solid));
  border: 1px solid color-mix(in srgb, var(--case-edge) 70%, var(--border));
  border-radius: var(--radius);
  padding: 14px 16px 18px;
  box-shadow:
    inset 0 4px 8px rgba(0, 0, 0, 0.35),
    inset 0 1px 2px rgba(0, 0, 0, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.18);
  transition: border-color var(--transition);
}

.display--error {
  border-color: color-mix(in srgb, var(--danger) 55%, transparent);
}

.display__toolbar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.spacer {
  flex: 1;
}

.icon-btn {
  border: none;
  background: transparent;
  color: var(--text-dim);
  font-size: 1.2rem;
  width: 38px;
  height: 34px;
  border-radius: 10px;
  cursor: pointer;
  transition:
    background var(--transition),
    color var(--transition);
}

.icon-btn:hover {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--text);
}

.icon-btn.active {
  background: color-mix(in srgb, var(--accent) 28%, transparent);
  color: var(--accent);
}

.display__toolbar .icon-btn:has(+ .mode-btn) {
  color: var(--danger);
}

.display__toolbar .icon-btn:has(+ .mode-btn):hover {
  background: color-mix(in srgb, var(--danger) 18%, transparent);
  color: var(--danger);
}

.icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.icon-btn:focus-visible,
.mode-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.mode-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 54px;
  height: 34px;
  padding: 0 9px;
  border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border));
  border-radius: 10px;
  background: color-mix(in srgb, var(--accent) 9%, transparent);
  color: var(--accent);
  cursor: pointer;
  font: 800 0.72rem var(--font-mono);
  letter-spacing: 0.05em;
  transition:
    background var(--transition),
    color var(--transition),
    transform 120ms ease;
}

.mode-btn small {
  color: var(--text-dim);
  font-size: 0.55rem;
  letter-spacing: 0;
}

.mode-btn:hover,
.mode-btn.active {
  background: color-mix(in srgb, var(--accent) 22%, transparent);
  color: var(--text);
}

.mode-btn:active {
  transform: scale(0.95);
}

.display__screen {
  min-height: 74px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
}

.display__input {
  font-family: var(--font-mono);
  font-size: clamp(1.8rem, 7vw, 2.6rem);
  font-weight: 600;
  line-height: 1.1;
  width: 100%;
  text-align: right;
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: none;
}
.display__input::-webkit-scrollbar {
  display: none;
}

.display__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-height: 24px;
  margin-top: 6px;
}

.display__action {
  padding: 4px 8px;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: transparent;
  color: var(--text-dim);
  cursor: pointer;
  font: 0.68rem var(--font);
}

.display__action:hover,
.display__action:focus-visible {
  border-color: var(--accent);
  color: var(--accent);
}

.display__action-message {
  color: var(--accent);
  font-size: 0.68rem;
}

.display__preview {
  font-family: var(--font-mono);
  font-size: 1.1rem;
  color: var(--accent);
  margin-top: 4px;
}

.display__preview.is-error {
  color: var(--danger);
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 420px) {
  .display {
    min-width: 0;
    padding: 12px 10px 16px;
  }

  .display__toolbar {
    gap: 4px;
  }

  .icon-btn {
    width: 32px;
    padding: 0;
  }

  .mode-btn {
    min-width: 50px;
    padding: 0 6px;
  }
}
</style>
